import PropTypes from 'prop-types'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-light-svg-icons'

import { Button, DashboardModule } from '@domain-group/suit-theme'

import { useFilters } from '@domain-group/react-hooks'

import useAgencyProvider from '@hooks/useAgencyProvider'
import useAgencyProviders from '@hooks/useAgencyProviders'
import useServices from '@hooks/useServices'

import DashboardModuleFilters from '@components/DashboardModuleFilters'
import ScopedConditionalContent from '@components/ScopedConditionalContent'

import AgencyProviderGroup from './AgencyProviderGroup'

const buildPageFilterFields = () => {
  const style = {
    marginBottom: 16,
  }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'provider_string',
      label: 'Filter by Provider Title',
    },
  ]
}
interface AgencyProviderProps {
  agencyId: number,
}

const AgencyProviders = (props: AgencyProviderProps) => {
  const { agencyId } = props

  const filterPayload = useFilters()
  const { pageFilters } = filterPayload

  const agencyProvidersPayload = useAgencyProviders({
    filters: {
      agency_id: agencyId,
      ...pageFilters,
    },
    performHttpRequests: !!agencyId,
    sortKey: 'provider_title',
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    // @ts-expect-error TS2339
    filteredAgencyProviderCount,
    groupedProvidersByService,
    hasFilteredAgencyProviders,
    loading: loadingAgencyProviders,
  } = agencyProvidersPayload

  const {
    callbacks: { createOrEditAgencyProvider },
  } = useAgencyProvider({ agency_id: agencyId })

  const { filteredServices, loading: loadingServices } = useServices()

  const loading = loadingAgencyProviders || loadingServices

  return (
    <DashboardModule
      boxProps={{ id: 'realhub-nav-providers' }}
      contentBoxProps={{ flexDirection: 'column' }}
      headerRight={hasFilteredAgencyProviders && <DashboardModule.FilterHeader {...filterPayload} />}
      loading={loading}
      title="Providers"
    >
      <DashboardModuleFilters
        filterFields={buildPageFilterFields()}
        {...filterPayload}
      />

      {!loading && !hasFilteredAgencyProviders && (
        <DashboardModule.BlankState>
          <DashboardModule.BlankState.Title>
            No Providers Present
          </DashboardModule.BlankState.Title>

          <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
            <DashboardModule.BlankState.Paragraph>
              Please adjust your filters or add a Provider below.
            </DashboardModule.BlankState.Paragraph>
          </ScopedConditionalContent>
        </DashboardModule.BlankState>
      )}

      {!loading && hasFilteredAgencyProviders && (
        <DashboardModule.ScrollableSection>
          {filteredServices.map((service) => {
            // @ts-expect-error TS7053
            const groupedAgencyProviders = groupedProvidersByService[service.id]

            if (!groupedAgencyProviders) return null

            return (
              <AgencyProviderGroup
                agencyId={agencyId}
                key={service.id}
                groupedAgencyProviders={groupedAgencyProviders}
                service={service}
              />
            )
          })}
        </DashboardModule.ScrollableSection>
      )}

      <DashboardModule.LoadMoreFooter
        boxProps={{ borderTop: loading ? 'none' : '1px solid' }}
        callbacks={{ loadMore }}
        canLoadMore={canLoadMore}
        entityCount={filteredAgencyProviderCount}
        loading={loading}
      >
        <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
          <Button
            buttonStyle="secondaryEdit"
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => createOrEditAgencyProvider()}
            // @ts-expect-error TS2322
            size={['medium', 'small']}
            width={['100%', 'fit-content']}
          >
            Add Provider
          </Button>
        </ScopedConditionalContent>
      </DashboardModule.LoadMoreFooter>
    </DashboardModule>
  )
}

AgencyProviders.propTypes = {
  agencyId: PropTypes.number,
}

export default AgencyProviders
